import React, { useState } from 'react';
import { servicesData, additionalProjects } from '../data/servicesData';
import '../styles/components/Portfolio.scss';

const Portfolio = () => {
  const [expandedProjects, setExpandedProjects] = useState({});
  const [visibleProjects, setVisibleProjects] = useState(6);
  const [activeFilter, setActiveFilter] = useState('all');

  // Combine all projects from services and additional projects
  const allProjects = [
    ...servicesData.flatMap(service => 
      (service.detailedInfo?.projects || []).map(project => ({
        ...project,
        category: service.title,
        categoryColor: service.color,
        serviceId: service.id
      }))
    ),
    ...additionalProjects.map(project => ({
      ...project,
      serviceId: 'additional'
    }))
  ];

  // Get unique categories for filtering
  const categories = [
    { id: 'all', name: 'All Projects', color: '#1a365d' },
    ...servicesData.map(service => ({
      id: service.id,
      name: service.title,
      color: service.color
    }))
  ];

  // Filter projects based on active filter
  const filteredProjects = activeFilter === 'all' 
    ? allProjects 
    : allProjects.filter(project => project.serviceId === activeFilter);

  const displayedProjects = filteredProjects.slice(0, visibleProjects);
  const hasMoreProjects = visibleProjects < filteredProjects.length;

  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const loadMoreProjects = () => {
    setVisibleProjects(prev => prev + 6);
  };

  const handleFilterClick = (categoryId) => {
    setActiveFilter(categoryId);
    setVisibleProjects(6);
    setExpandedProjects({});
  };

  // Get the current category name for display
  const getCurrentCategoryName = () => {
    if (activeFilter === 'all') return 'All Projects';
    const category = categories.find(cat => cat.id === activeFilter);
    return category ? category.name : 'Projects';
  };

  // Inline styles for background images
  const heroStyle = {
    backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.85) 0%, rgba(229, 62, 62, 0.7) 50%, rgba(221, 107, 32, 0.5) 100%), url('/images/services-hero.jpg')`
  };

  const statsStyle = {
    backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.9) 0%, rgba(26, 54, 93, 0.95) 100%), url('/images/student-mentorship.jpg')`
  };

  const portfolioStats = [
    { number: '50+', label: 'Projects Completed', icon: 'fas fa-project-diagram' },
    { number: '25+', label: 'Satisfied Clients', icon: 'fas fa-users' },
    { number: '40+', label: 'Districts Covered', icon: 'fas fa-map-marker-alt' },
    { number: '40,000+', label: 'Respondents Reached', icon: 'fas fa-chart-line' }
  ];

  return (
    <div className="portfolio-page">
      {/* Modern Hero Section with Background Image */}
      <section className="portfolio-hero" style={heroStyle}>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="badge">
                <span>Our Portfolio</span>
              </div>
              <h1>
                <span className="highlight-text">Success</span> Stories That
                <br />
                <span className="highlight-text">Speak</span> Volumes
              </h1>
              <p className="hero-description">
                Discover the impactful projects and innovative solutions we've delivered across various sectors. 
                Each project represents our commitment to excellence, measurable results, and sustainable impact.
              </p>
              
              <div className="hero-actions">
                <a href="#portfolio-grid" className="btn-primary">
                  <span>Explore Projects</span>
                  <i className="fas fa-arrow-down"></i>
                </a>
                <a href="/services" className="btn-secondary">
                  <i className="fas fa-cog"></i>
                  Our Services
                </a>
              </div>
            </div>

            <div className="hero-visual">
              <div className="stats-grid">
                {portfolioStats.map((stat, index) => (
                  <div key={index} className="stat-card" style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className="stat-icon">
                      <i className={stat.icon}></i>
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">{stat.number}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="scroll-indicator">
          <div className="scroll-line"></div>
          <span>Scroll to explore</span>
        </div>
      </section>

      {/* Portfolio Stats Section */}
      <section className="portfolio-stats" style={statsStyle}>
        <div className="container">
          <div className="section-header">
            <h2>Our Impact in Numbers</h2>
            <p>Quantifying the success and reach of our consulting projects across various domains</p>
          </div>
        </div>
      </section>

      {/* Portfolio Content */}
      <section className="portfolio-content" id="portfolio-grid">
        <div className="container">
          {/* Filter Header */}
          <div className="portfolio-header">
            <h2 className="portfolio-title">
              {getCurrentCategoryName()} 
              <span className="project-count"> ({filteredProjects.length} projects)</span>
            </h2>
            <p className="portfolio-subtitle">
              Browse through our completed projects and see the impact we've made across different sectors
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="portfolio-filters">
            {categories.map(category => (
              <button
                key={category.id}
                className={`filter-btn ${activeFilter === category.id ? 'active' : ''}`}
                onClick={() => handleFilterClick(category.id)}
                style={{
                  '--category-color': category.color,
                  backgroundColor: activeFilter === category.id ? category.color : 'transparent',
                  color: activeFilter === category.id ? '#fff' : category.color
                }}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Portfolio Grid */}
          <div className="portfolio-grid">
            {displayedProjects.length > 0 ? (
              displayedProjects.map((project) => (
                <div key={project.id} className="portfolio-project-card">
                  <div className="project-category" style={{ backgroundColor: project.categoryColor }}>
                    {project.category}
                  </div>
                  
                  <div className="project-content">
                    <div className="project-header">
                      <h3>{project.title}</h3>
                      <div className="project-meta">
                        <span className="project-year">{project.year}</span>
                        <span className="project-client">{project.client}</span>
                      </div>
                    </div>

                    <div className="project-details">
                      <div className="project-location">
                        <i className="fas fa-map-marker-alt"></i>
                        {project.location}
                      </div>
                      <div className="project-scope">
                        <i className="fas fa-chart-bar"></i>
                        {project.scope}
                      </div>
                      <div className="project-duration">
                        <i className="fas fa-clock"></i>
                        {project.duration}
                      </div>
                    </div>

                    <p className="project-description">
                      {project.description}
                    </p>

                    <div className={`project-details-expandable ${expandedProjects[project.id] ? 'expanded' : ''}`}>
                      <div className="scope-of-work">
                        <h4>Scope of Work:</h4>
                        <ul>
                          {(project.scopeOfWork || []).map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="project-outcomes">
                        <h4>Key Outcomes:</h4>
                        <ul>
                          {(project.outcomes || []).map((outcome, index) => (
                            <li key={index}>{outcome}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <button 
                      className="read-more-btn"
                      onClick={() => toggleProjectExpansion(project.id)}
                      style={{ '--category-color': project.categoryColor }}
                    >
                      {expandedProjects[project.id] ? 'Read Less' : 'Read More'}
                      <i className={`fas fa-chevron-${expandedProjects[project.id] ? 'up' : 'down'}`}></i>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-projects-message">
                <i className="fas fa-search"></i>
                <h3>No projects found</h3>
                <p>There are no projects in this category at the moment.</p>
              </div>
            )}
          </div>

          {/* Load More Button */}
          {hasMoreProjects && (
            <div className="load-more-container">
              <button className="load-more-btn" onClick={loadMoreProjects}>
                View More Projects
                <i className="fas fa-arrow-down"></i>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="portfolio-cta" style={statsStyle}>
        <div className="container">
          <div className="cta-content">
            <h2>Have a Project in Mind?</h2>
            <p>Let's collaborate to create your next success story. Our team is ready to bring your vision to life.</p>
            <div className="cta-buttons">
              <a href="/contact" className="btn btn-primary">
                <i className="fas fa-rocket"></i>
                Start Your Project
              </a>
              <a href="/services" className="btn btn-secondary">
                <i className="fas fa-cog"></i>
                Explore Services
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;